'use client'

import React from 'react'

/**
 * A2P 10DLC web opt-in consent field.
 *
 * Carrier/CTIA rules for web-form SMS consent require an EXPLICIT, un-prechecked
 * checkbox with a disclosure adjacent to it that states: message purpose, frequency,
 * msg&data-rate notice, HELP/STOP instructions, links to Terms + Privacy, and that
 * consent is not a condition of purchase. The phone field alone never counts as consent.
 *
 * This is a controlled component — the parent form owns the boolean so the value can be
 * submitted (and stored as consent evidence) alongside the rest of the payload.
 *
 * `termsUrl` / `privacyUrl` are optional: until the site has public Terms of Service and
 * Privacy Policy pages, the disclosure renders them as plain (non-linked) text rather than
 * shipping broken links a carrier reviewer would flag. Pass the URLs once the pages exist.
 */

export const SMS_CONSENT_VERSION = 'chr-sms-consent-v1'

export function SmsConsentField({
  checked,
  onChange,
  termsUrl,
  privacyUrl,
  id = 'sms-consent',
}: {
  checked: boolean
  onChange: (next: boolean) => void
  termsUrl?: string
  privacyUrl?: string
  id?: string
}) {
  const terms = termsUrl ? (
    <a href={termsUrl} style={{ color: '#009BAB', textDecoration: 'underline' }}>
      Terms of Service
    </a>
  ) : (
    <span>Terms of Service</span>
  )
  const privacy = privacyUrl ? (
    <a href={privacyUrl} style={{ color: '#009BAB', textDecoration: 'underline' }}>
      Privacy Policy
    </a>
  ) : (
    <span>Privacy Policy</span>
  )

  return (
    <label
      htmlFor={id}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        margin: '4px 0 14px',
        cursor: 'pointer',
        fontFamily: 'var(--font-outfit-family), sans-serif',
        fontSize: '13px',
        lineHeight: 1.45,
        color: '#4b4b49',
      }}
    >
      <input
        id={id}
        name="sms_consent"
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: '2px', width: '16px', height: '16px', flex: '0 0 auto', accentColor: '#009BAB' }}
      />
      <span>
        Yes, text me about my project. I agree to receive appointment- and estimate-related text
        messages from Craft Home Remodel at the number provided. Message frequency varies; msg &amp;
        data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of
        purchase. See our {terms} and {privacy}.
      </span>
    </label>
  )
}
